import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminSectionNav from './AdminSectionNav';
import { loadOrgAddressDefaults } from '../utils/orgAddressDefaults';
import { isOrgAdminRole } from '../utils/roleUtils';
import OrgPrintChrome from './OrgPrintChrome';
import GstReturns from './GstReturns';
import Receivables from './Receivables';
import FinancialStatements from './FinancialStatements';
import EditLog from './EditLog';
import DataImportExport from './DataImportExport';
import OpeningBalances from './OpeningBalances';
import Tds from './Tds';
import Payables from './Payables';
import Expenses from './Expenses';
import Vouchers from './Vouchers';
import { showDate } from '../utils/dates';
import BankReconciliation from './BankReconciliation';
import { hasAnyRole } from '../utils/permissionUtils';
import useSectionTab from '../hooks/useSectionTab';
import { todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import ModuleAlerts from './ModuleAlerts';
import { formatDateTime, formatDate } from '../utils/dateFormat';
// The sections of Accounts under their group headings, in menu order. Which ones a person sees depends on their role
// (see visibleSections in the component). Each key is also the section's address and its help topic.
const ACCOUNTS_SECTION_GROUPS = [
  {
    group: 'Money in and out',
    items: [
      { key: 'receivables', label: 'Receivables', icon: 'bi-arrow-down-left-circle' },
      { key: 'payables', label: 'Payables', icon: 'bi-arrow-up-right-circle' },
      { key: 'vendor-payments', label: 'Vendor Payments', icon: 'bi-credit-card' },
      { key: 'expenses', label: 'Expenses', icon: 'bi-wallet2' }
    ]
  },
  {
    group: 'Bank and cash',
    items: [
      { key: 'bank-accounts', label: 'Bank Accounts', icon: 'bi-bank' },
      { key: 'bank-reconciliation', label: 'Bank Reconciliation', icon: 'bi-check2-square' },
      { key: 'cash-in-hand', label: 'Cash-in-Hand', icon: 'bi-cash-stack' },
      { key: 'transfers', label: 'Transfers & Journals', icon: 'bi-arrow-left-right' }
    ]
  },
  {
    group: 'Books',
    items: [
      { key: 'ledgers', label: 'Ledgers', icon: 'bi-journal-text' },
      { key: 'financial-statements', label: 'Financial Statements', icon: 'bi-graph-up' },
      { key: 'opening-balances', label: 'Opening Balances', icon: 'bi-flag' },
      { key: 'edit-log', label: 'Edit Log', icon: 'bi-clock-history' }
    ]
  },
  {
    group: 'Tax',
    items: [
      { key: 'gst-returns', label: 'GST Returns', icon: 'bi-file-earmark-text' },
      { key: 'tds', label: 'TDS', icon: 'bi-percent' }
    ]
  },
  {
    group: 'Data',
    items: [
      { key: 'import-export', label: 'Import / Export', icon: 'bi-arrow-down-up' }
    ]
  }
];
const ACCOUNTS_SECTIONS = ACCOUNTS_SECTION_GROUPS.flatMap((g) => g.items.map((i) => i.key));
// A first visit opens here; after that, the section last used
const ACCOUNTS_DEFAULT_SECTION = 'ledgers';

function Accounts() {
  const { user } = useAuth();
  // Setting up ledgers, bank and cash accounts is the organization admin's; the finance roles only read them
  const canManageAccounts = !!user?.isSystemAdmin || (user?.roles || []).some(isOrgAdminRole);
  // Opening balances: the Org Admin and the Finance Manager
  const canEnterOpeningBalances = canManageAccounts || hasAnyRole(user, ['FIN_MGR']);
  // TDS: only when the organization deducts it, for the finance roles
  const canUseTds = canManageAccounts || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']);
  // Cancelling a vendor payment recorded by mistake: the Org Admin and the Finance Manager
  const canCancelPayments = canManageAccounts || hasAnyRole(user, ['FIN_MGR']);
  // Bank reconciliation and expenses: the finance roles
  const canReconcile = canManageAccounts || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']);
  const canManageTds = canManageAccounts || hasAnyRole(user, ['FIN_MGR']);
  // The edit log: the Org Admin (also in Org Admin) and the Finance Manager, often the auditor's login
  const canSeeEditLog = canManageAccounts || hasAnyRole(user, ['FIN_MGR']);
  // The Tally export and the customer and vendor import (also in Org Admin): the finance roles, whose accountants work in Tally
  const canExchangeData = canManageAccounts || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']);
  const [tdsEnabled, setTdsEnabled] = useState(false);
  const [tdsSuggestion, setTdsSuggestion] = useState(null);
  const [tdsAmount, setTdsAmount] = useState('');
  const [tdsTouched, setTdsTouched] = useState(false);
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/accounts/<section>)
  const [activeTab, setActiveTab] = useSectionTab('/accounts', ACCOUNTS_SECTIONS, ACCOUNTS_DEFAULT_SECTION);
  // Only what this person's role can use, as the tabs were: no empty groups
  const sectionAllowed = {
    'bank-reconciliation': canReconcile,
    expenses: canReconcile,
    transfers: canReconcile,
    'edit-log': canSeeEditLog,
    'opening-balances': canEnterOpeningBalances,
    'import-export': canExchangeData,
    tds: tdsEnabled && canUseTds
  };
  // An address for a section this role does not have (an old link, or typed in) opens the usual first section instead of nothing.
  // TDS depends on a setting that loads a moment later, so it is left to the menu.
  const sectionNotAllowed = activeTab !== 'tds' && sectionAllowed[activeTab] === false;
  useEffect(() => {
    if (sectionNotAllowed) setActiveTab(ACCOUNTS_DEFAULT_SECTION);
  }, [sectionNotAllowed, setActiveTab]);
  // A customer's or vendor's ledger asked for from Receivables or Payables, opened in Financial Statements
  const [ledgerRequest, setLedgerRequest] = useState(null);
  const openPartyLedger = (code) => {
    setLedgerRequest({ code });
    setActiveTab('financial-statements');
  };
  
  // Ledgers state
  const [ledgers, setLedgers] = useState([]);
  const [ledgerFilter, setLedgerFilter] = useState('ALL'); // ALL, ASSET, LIABILITY, EQUITY, REVENUE, EXPENSE
  const [showLedgerTransactions, setShowLedgerTransactions] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerFormData, setLedgerFormData] = useState({
    name: '',
    type: '',
    category: '',
    description: '',
    openingBalance: '0.00'
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // Bank Accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [bankAccountFormData, setBankAccountFormData] = useState({
    accountName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    swiftCode: '',
    branchName: '',
    branchAddress: '',
    accountType: '',
    openingBalance: '0.00',
    currency: ''
  });
  
  // Cash-in-Hand state
  const [cashInHandList, setCashInHandList] = useState([]);
  const [showCashInHandModal, setShowCashInHandModal] = useState(false);
  const [selectedCashInHand, setSelectedCashInHand] = useState(null);
  const [cashInHandFormData, setCashInHandFormData] = useState({
    locationName: '',
    locationDescription: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    openingBalance: '0.00',
    currency: ''
  });
  
  const [organizationCurrency, setOrganizationCurrency] = useState('USD');
  const [orgCountry, setOrgCountry] = useState('');
  const [orgState, setOrgState] = useState('');
  
  const [showViewPrintModal, setShowViewPrintModal] = useState(false);
  const [viewPrintData, setViewPrintData] = useState(null);
  const [viewPrintType, setViewPrintType] = useState(null);

  // Vendor Payments state
  const [vendorPayments, setVendorPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [currentAssetAccounts, setCurrentAssetAccounts] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedVendorForPayment, setSelectedVendorForPayment] = useState(null);
  const [openPOs, setOpenPOs] = useState([]);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [loadingOpenPOs, setLoadingOpenPOs] = useState(false);
  // The vendor's open bills in the payment dialog, and how much of the payment goes to each (oldest first until changed)
  const [openBills, setOpenBills] = useState([]);
  const [billAmounts, setBillAmounts] = useState({});
  const [billsTouched, setBillsTouched] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    vendor: null,
    paymentDate: todayISO(),
    amount: '',
    paymentMethod: 'CHECK',
    sourceAccount: null,
    referenceNumber: '',
    notes: '',
    poApplications: []
  });
  
  // Help pane state

  useEffect(() => {
    fetchOrganizationCurrency();
    if (canUseTds) {
      axios.get('/api/tds/settings').then((r) => setTdsEnabled(Boolean(r.data?.enabled))).catch(() => setTdsEnabled(false));
    }
  }, [canUseTds]);

  // The TDS to keep back from the payment being entered, worked out from the vendor's section, PAN and the year so far
  const paymentVendorId = paymentFormData.vendor?.id;
  const paymentAmount = paymentFormData.amount;
  const paymentDate = paymentFormData.paymentDate;
  useEffect(() => {
    if (!tdsEnabled || !showPaymentModal || !paymentVendorId || !(parseFloat(paymentAmount) > 0)) {
      setTdsSuggestion(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      axios.get('/api/tds/suggest', { params: { vendorId: paymentVendorId, amount: paymentAmount, date: paymentDate } })
        .then((r) => {
          const found = r.data && r.data.section ? r.data : null;
          setTdsSuggestion(found);
          if (!tdsTouched) setTdsAmount(found ? String(found.tds) : '');
        })
        .catch(() => setTdsSuggestion(null));
    }, 350);
    return () => clearTimeout(timer);
  }, [tdsEnabled, showPaymentModal, paymentVendorId, paymentAmount, paymentDate, tdsTouched]);

  useEffect(() => {
    if (activeTab === 'ledgers') {
      fetchLedgers();
      // Reset transactions view when switching away from ledgers tab
      if (showLedgerTransactions) {
        setShowLedgerTransactions(false);
        setLedgerTransactions([]);
        setSelectedLedger(null);
      }
    } else if (activeTab === 'bank-accounts') {
      fetchBankAccounts();
    } else if (activeTab === 'cash-in-hand') {
      fetchCashInHand();
    } else if (activeTab === 'vendor-payments' || activeTab === 'payables') {
      fetchVendorPayments();
      fetchVendors();
      fetchCurrentAssetAccounts();
    }
  }, [activeTab]);

  const fetchOrganizationCurrency = async () => {
    try {
      const response = await axios.get(`/api/organizations/${user?.organizationId}`);
      const currency = response.data?.defaultCurrency || 'USD';
      setOrganizationCurrency(currency);
      setOrgCountry(response.data?.country || '');
      setOrgState(response.data?.state || '');
    } catch (error) {
      console.error('Error fetching organization currency:', error);
    }
  };

  const handleCreateCashInHand = async () => {
    let country = orgCountry;
    let state = orgState;
    if (!country && !state) {
      const defaults = await loadOrgAddressDefaults(user?.organizationId);
      country = defaults.country;
      state = defaults.state;
      setOrgCountry(country);
      setOrgState(state);
    }
    setCashInHandFormData({
      locationName: '',
      locationDescription: '',
      address: '',
      city: '',
      state,
      country,
      postalCode: '',
      openingBalance: '0.00',
      currency: organizationCurrency
    });
    setSelectedCashInHand(null);
    setShowCashInHandModal(true);
  };

  const fetchLedgers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts/ledgers');
      setLedgers(response.data);
    } catch (error) {
      toast.error('Error fetching ledgers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts/bank-accounts');
      setBankAccounts(response.data);
    } catch (error) {
      toast.error('Error fetching bank accounts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashInHand = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts/cash-in-hand');
      setCashInHandList(response.data);
    } catch (error) {
      toast.error('Error fetching cash-in-hand');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoriesByType = async (type) => {
    if (!type) {
      return [];
    }
    try {
      const response = await axios.get(`/api/accounts/ledger-categories/type/${type}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories by type:', error);
      return [];
    }
  };

  const handleCreateLedger = () => {
    setLedgerFormData({
      name: '',
      type: '',
      category: '',
      description: '',
      openingBalance: '0.00'
    });
    setShowLedgerModal(true);
  };

  const handleSaveLedger = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: ledgerFormData.name.trim(),
        type: ledgerFormData.type,
        category: ledgerFormData.category || null,
        description: ledgerFormData.description.trim() || null,
        openingBalance: parseFloat(ledgerFormData.openingBalance) || 0
      };

      await axios.post('/api/accounts/ledgers', payload);
      toast.success('Ledger created successfully');
      setShowLedgerModal(false);
      fetchLedgers();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error creating ledger';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating ledger');
    }
  };

  const handleLedgerTypeChange = async (type) => {
    setLedgerFormData({ ...ledgerFormData, type, category: '' });
    if (type) {
      const categories = await fetchCategoriesByType(type);
      setAvailableCategories(categories);
    } else {
      setAvailableCategories([]);
    }
  };


  const handleInitGeneralLedger = async () => {
    try {
      await axios.post('/api/accounts/ledgers/init-general-ledger');
      toast.success('General Ledger initialized successfully');
      fetchLedgers();
    } catch (error) {
      toast.error(error.response?.data || 'Error initializing general ledger');
    }
  };

  const handleCreateBankAccount = () => {
    setBankAccountFormData({
      accountName: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      swiftCode: '',
      branchName: '',
      branchAddress: '',
      accountType: '',
      openingBalance: '0.00',
      currency: organizationCurrency
    });
    setSelectedBankAccount(null);
    setShowBankAccountModal(true);
  };

  const handleEditBankAccount = (bankAccount) => {
    setBankAccountFormData({
      accountName: bankAccount.accountName || '',
      bankName: bankAccount.bankName || '',
      accountNumber: bankAccount.accountNumber || '',
      ifscCode: bankAccount.ifscCode || '',
      swiftCode: bankAccount.swiftCode || '',
      branchName: bankAccount.branchName || '',
      branchAddress: bankAccount.branchAddress || '',
      accountType: bankAccount.accountType || '',
      openingBalance: bankAccount.openingBalance || '0.00',
      currency: bankAccount.currency || organizationCurrency
    });
    setSelectedBankAccount(bankAccount);
    setShowBankAccountModal(true);
  };

  const handleDeleteBankAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) {
      return;
    }
    try {
      await axios.delete(`/api/accounts/bank-accounts/${id}`);
      toast.success('Bank account deleted successfully');
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data || 'Error deleting bank account');
    }
  };

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...bankAccountFormData,
        openingBalance: parseFloat(bankAccountFormData.openingBalance) || 0,
        currentBalance: parseFloat(bankAccountFormData.openingBalance) || 0
      };
      
      if (selectedBankAccount) {
        await axios.put(`/api/accounts/bank-accounts/${selectedBankAccount.id}`, payload);
        toast.success('Bank account updated successfully');
      } else {
        await axios.post('/api/accounts/bank-accounts', payload);
        toast.success('Bank account created successfully');
      }
      setShowBankAccountModal(false);
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data || 'Error saving bank account');
    }
  };

  const handleEditCashInHand = (cashInHand) => {
    setCashInHandFormData({
      locationName: cashInHand.locationName || '',
      locationDescription: cashInHand.locationDescription || '',
      address: cashInHand.address || '',
      city: cashInHand.city || '',
      state: cashInHand.state || '',
      country: cashInHand.country || '',
      postalCode: cashInHand.postalCode || '',
      openingBalance: cashInHand.openingBalance || '0.00',
      currency: cashInHand.currency || organizationCurrency
    });
    setSelectedCashInHand(cashInHand);
    setShowCashInHandModal(true);
  };

  const handleDeleteCashInHand = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cash-in-hand location?')) {
      return;
    }
    try {
      await axios.delete(`/api/accounts/cash-in-hand/${id}`);
      toast.success('Cash-in-hand deleted successfully');
      fetchCashInHand();
    } catch (error) {
      toast.error(error.response?.data || 'Error deleting cash-in-hand');
    }
  };

  const handleSaveCashInHand = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...cashInHandFormData,
        openingBalance: parseFloat(cashInHandFormData.openingBalance) || 0,
        currentBalance: parseFloat(cashInHandFormData.openingBalance) || 0
      };
      
      if (selectedCashInHand) {
        await axios.put(`/api/accounts/cash-in-hand/${selectedCashInHand.id}`, payload);
        toast.success('Cash-in-hand updated successfully');
      } else {
        await axios.post('/api/accounts/cash-in-hand', payload);
        toast.success('Cash-in-hand created successfully');
      }
      setShowCashInHandModal(false);
      fetchCashInHand();
    } catch (error) {
      toast.error(error.response?.data || 'Error saving cash-in-hand');
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return `${organizationCurrency} 0.00`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${organizationCurrency} ${numAmount.toFixed(2)}`;
  };

  const handleViewTransactions = async (ledger) => {
    setSelectedLedger(ledger);
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`/api/accounts/ledgers/${ledger.id}/transactions`);
      setLedgerTransactions(response.data || []);
      setShowLedgerTransactions(true);
    } catch (error) {
      toast.error('Error fetching transactions');
      console.error(error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleBackToLedgers = () => {
    setShowLedgerTransactions(false);
    setLedgerTransactions([]);
    setSelectedLedger(null);
  };

  const formatDate = (dateString) => (dateString ? formatDateTime(dateString) : '-');



  const handleViewPrint = (data, type) => {
    setViewPrintData(data);
    setViewPrintType(type);
    setShowViewPrintModal(true);
  };

  const handleViewPrintPrint = () => {
    window.print();
  };


  // Vendor Payments functions
  const fetchVendorPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts/vendor-payments');
      setVendorPayments(response.data);
    } catch (error) {
      toast.error('Error fetching vendor payments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/inventory/vendors/active');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchCurrentAssetAccounts = async () => {
    try {
      const response = await axios.get('/api/accounts/ledgers');
      // Filter for Current Asset accounts (Bank Accounts and Cash-in-Hand)
      const currentAssets = response.data.filter(ledger => 
        ledger.type === 'ASSET' && 
        ledger.category === 'Current Asset' &&
        (ledger.bankAccount != null || ledger.cashInHand != null)
      );
      setCurrentAssetAccounts(currentAssets);
    } catch (error) {
      console.error('Error fetching current asset accounts:', error);
    }
  };

  const fetchOpenPOsForVendor = async (vendorId) => {
    try {
      setLoadingOpenPOs(true);
      const response = await axios.get(`/api/accounts/vendor-payments/vendor/${vendorId}/open-pos`);
      setOpenPOs(response.data.openPOs || []);
      setOutstandingBalance(response.data.outstandingBalance || 0);
    } catch (error) {
      toast.error('Error fetching open POs for vendor');
      console.error(error);
    } finally {
      setLoadingOpenPOs(false);
    }
  };

  const fetchOpenBillsForVendor = async (vendorId) => {
    try {
      const response = await axios.get(`/api/accounts/payables/vendors/${vendorId}`);
      setOpenBills(response.data.bills || []);
    } catch (error) {
      setOpenBills([]);
    }
  };

  // Until the user changes it, the payment settles the oldest bills first
  useEffect(() => {
    if (billsTouched) return;
    let left = parseFloat(paymentFormData.amount) || 0;
    const next = {};
    for (const b of openBills) {
      if (left <= 0) break;
      const take = Math.min(left, Number(b.outstanding));
      next[b.id] = Math.round(take * 100) / 100;
      left -= take;
    }
    setBillAmounts(next);
  }, [paymentFormData.amount, openBills, billsTouched]);

  /** Opens the payment dialog for a vendor (from Payables). */
  const payVendor = (vendorId) => {
    handleCreatePayment();
    const vendor = vendors.find((v) => v.id === Number(vendorId)) || { id: Number(vendorId) };
    setSelectedVendorForPayment(vendor);
    setPaymentFormData((prev) => ({ ...prev, vendor }));
    fetchOpenPOsForVendor(Number(vendorId));
    fetchOpenBillsForVendor(Number(vendorId));
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === parseInt(vendorId));
    setSelectedVendorForPayment(vendor);
    setPaymentFormData({ ...paymentFormData, vendor: vendor, poApplications: [] });
    setBillsTouched(false);
    setOpenBills([]);
    if (vendorId) {
      fetchOpenPOsForVendor(parseInt(vendorId));
      fetchOpenBillsForVendor(parseInt(vendorId));
    } else {
      setOpenPOs([]);
      setOutstandingBalance(0);
    }
  };

  const handlePOApplicationChange = (poId, appliedAmount) => {
    const paymentAmount = parseFloat(paymentFormData.amount) || 0;
    const poApps = [...paymentFormData.poApplications];
    const existingIndex = poApps.findIndex(app => app.poId === poId);
    
    // Calculate current total applied (excluding the one being changed)
    const currentTotal = poApps.reduce((sum, app) => {
      if (app.poId === poId) return sum; // Exclude the one being changed
      return sum + (app.appliedAmount || 0);
    }, 0);
    
    const newAppliedAmount = parseFloat(appliedAmount) || 0;
    const newTotal = currentTotal + newAppliedAmount;
    
    // Validate that new total doesn't exceed payment amount
    if (newAppliedAmount > 0 && newTotal > paymentAmount) {
      toast.error(`Total applied amount (${formatCurrency(newTotal)}) cannot exceed payment amount (${formatCurrency(paymentAmount)})`);
      return;
    }
    
    if (appliedAmount && newAppliedAmount > 0) {
      if (existingIndex >= 0) {
        poApps[existingIndex].appliedAmount = newAppliedAmount;
      } else {
        poApps.push({ poId: parseInt(poId), appliedAmount: newAppliedAmount });
      }
    } else {
      if (existingIndex >= 0) {
        poApps.splice(existingIndex, 1);
      }
    }
    
    setPaymentFormData({ ...paymentFormData, poApplications: poApps });
  };

  const handleCreatePayment = () => {
    setPaymentFormData({
      vendor: null,
      paymentDate: todayISO(),
      amount: '',
      paymentMethod: 'CHECK',
      sourceAccount: null,
      referenceNumber: '',
      notes: '',
      poApplications: []
    });
    setSelectedVendorForPayment(null);
    setOpenPOs([]);
    setOutstandingBalance(0);
    setOpenBills([]);
    setBillAmounts({});
    setBillsTouched(false);
    setTdsSuggestion(null);
    setTdsAmount('');
    setTdsTouched(false);
    setShowPaymentModal(true);
  };

  /** Cancels a payment recorded by mistake: its entries are reversed and the bills it paid are open again. */
  const handleCancelPayment = async (payment) => {
    const reason = window.prompt(`Cancel payment ${payment.paymentNumber} of ${formatCurrency(payment.amount)} to ${payment.vendor?.name || 'the vendor'}? `
      + 'Its entries are reversed today and the bills it paid are open again. Why?');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.warn('Say why the payment is being cancelled');
      return;
    }
    try {
      await axios.post(`/api/accounts/vendor-payments/${payment.id}/cancel`, { reason });
      toast.success(`Payment ${payment.paymentNumber} cancelled`);
      fetchVendorPayments();
      fetchLedgers();
    } catch (error) {
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not cancel the payment');
    }
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    
    if (!paymentFormData.vendor) {
      toast.error('Please select a vendor');
      return;
    }
    
    if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    if (!paymentFormData.sourceAccount) {
      toast.error('Please select a source account');
      return;
    }
    
    // Validate that total applied amount doesn't exceed payment amount
    const paymentAmount = parseFloat(paymentFormData.amount);
    const totalApplied = paymentFormData.poApplications.reduce((sum, app) => {
      return sum + (app.appliedAmount || 0);
    }, 0);
    
    if (totalApplied > paymentAmount) {
      toast.error(`Total amount applied to POs (${formatCurrency(totalApplied)}) cannot exceed payment amount (${formatCurrency(paymentAmount)})`);
      return;
    }
    const totalToBills = Object.values(billAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
    if (totalToBills > paymentAmount + 0.005) {
      toast.error(`The bills chosen (${formatCurrency(totalToBills)}) come to more than the payment (${formatCurrency(paymentAmount)})`);
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        vendor: { id: paymentFormData.vendor.id },
        paymentDate: paymentFormData.paymentDate,
        amount: parseFloat(paymentFormData.amount),
        paymentMethod: paymentFormData.paymentMethod,
        sourceAccount: { id: paymentFormData.sourceAccount.id },
        referenceNumber: paymentFormData.referenceNumber || null,
        notes: paymentFormData.notes || null,
        tdsAmount: tdsSuggestion && tdsAmount !== '' ? parseFloat(tdsAmount) : null,
        tdsSection: tdsSuggestion ? tdsSuggestion.section : null,
        poApplications: paymentFormData.poApplications.map(app => ({
          poId: app.poId,
          appliedAmount: app.appliedAmount
        })),
        billApplications: Object.entries(billAmounts)
          .filter(([, v]) => (parseFloat(v) || 0) > 0)
          .map(([billId, v]) => ({ billId: Number(billId), amount: parseFloat(v) }))
      };
      
      await axios.post('/api/accounts/vendor-payments', payload);
      toast.success('Vendor payment recorded successfully');
      setShowPaymentModal(false);
      fetchVendorPayments();
      fetchLedgers(); // Refresh ledgers to update balances
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data || error.message || 'Unknown error';
      toast.error('Error recording payment: ' + (typeof errorMessage === 'string' ? errorMessage : 'Unknown error'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getLedgerTypeBadge = (type) => {
    const badges = {
      'ASSET': 'success',
      'LIABILITY': 'danger',
      'EQUITY': 'info',
      'REVENUE': 'primary',
      'EXPENSE': 'warning'
    };
    return badges[type] || 'secondary';
  };

  const filteredLedgers = ledgerFilter === 'ALL' 
    ? ledgers 
    : ledgers.filter(l => l.type === ledgerFilter);

  const visibleSections = ACCOUNTS_SECTION_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((item) => sectionAllowed[item.key] !== false) }))
    .filter((g) => g.items.length > 0);

  return (
    <Container fluid className="mt-4 admin-page-shell">
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <div className="d-flex admin-tab-layout">
          <AdminSectionNav
            sections={visibleSections}
            activeKey={activeTab}
            onSelect={setActiveTab}
            ariaLabel="Accounts sections"
          />
          <div className="flex-grow-1 admin-tab-content-col">
          {/* Alerts for Accounts (which ones, and for whom, is decided by the server; see alerts/alertRegistry.js) */}
          <ModuleAlerts module="accounts" section={activeTab} />
          <Tab.Content>
            {/* Ledgers Tab */}
            <Tab.Pane eventKey="ledgers">
              {showLedgerTransactions ? (
                // Ledger Transactions View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <Button variant="outline-secondary" onClick={handleBackToLedgers} className="me-2">
                        ← Back to Ledgers
                      </Button>
                      <h5 className="d-inline-block mb-0 ms-2">{selectedLedger?.name || 'Ledger'}</h5>
                    </div>
                  </div>
                  <div className="mb-3">
                    <Row>
                      <Col md={6}>
                        <p className="mb-1"><strong>Code:</strong> {selectedLedger?.code || '-'}</p>
                        <p className="mb-1"><strong>Type:</strong> <Badge bg={getLedgerTypeBadge(selectedLedger?.type)}>{selectedLedger?.type}</Badge></p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-1"><strong>Opening Balance:</strong> {formatCurrency(selectedLedger?.openingBalance)}</p>
                        <p className="mb-1"><strong>Current Balance:</strong> {formatCurrency(selectedLedger?.currentBalance)}</p>
                      </Col>
                    </Row>
                  </div>
                  <Card>
                    <Card.Body>
                      {loadingTransactions ? (
                        <div className="d-flex justify-content-center">
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : ledgerTransactions.length === 0 ? (
                        <Alert variant="info">
                          No transactions found for this ledger.
                        </Alert>
                      ) : (
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Reference</th>
                              <th>Description</th>
                              <th>Type</th>
                              <th>Debit Ledger</th>
                              <th>Credit Ledger</th>
                              <th>Amount</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ledgerTransactions.map((transaction) => {
                              const isDebit = transaction.debitLedger?.id === selectedLedger?.id;
                              const isCredit = transaction.creditLedger?.id === selectedLedger?.id;
                              return (
                                <tr key={transaction.id}>
                                  <td>{formatDate(transaction.transactionDate)}</td>
                                  <td>{transaction.referenceNumber || '-'}</td>
                                  <td>{transaction.description || '-'}</td>
                                  <td>
                                    {transaction.transactionType && (
                                      <Badge bg="secondary">{transaction.transactionType}</Badge>
                                    )}
                                  </td>
                                  <td>
                                    {transaction.debitLedger?.name || '-'}
                                    {isDebit && <Badge bg="danger" className="ms-2">DR</Badge>}
                                  </td>
                                  <td>
                                    {transaction.creditLedger?.name || '-'}
                                    {isCredit && <Badge bg="success" className="ms-2">CR</Badge>}
                                  </td>
                                  <td>
                                    <strong>{formatCurrency(transaction.amount)}</strong>
                                  </td>
                                  <td>{transaction.notes || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ) : (
                // Ledgers List View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <p className="text-muted mb-0">View all organization ledgers</p>
                      <Form.Select
                        style={{ width: 'auto' }}
                        value={ledgerFilter}
                        onChange={(e) => setLedgerFilter(e.target.value)}
                      >
                        <option value="ALL">All Types</option>
                        <option value="ASSET">Assets</option>
                        <option value="LIABILITY">Liabilities</option>
                        <option value="EQUITY">Equity</option>
                        <option value="REVENUE">Revenue</option>
                        <option value="EXPENSE">Expense</option>
                      </Form.Select>
                    </div>
                    {canManageAccounts && (
                    <Button variant="primary" onClick={handleCreateLedger}>
                      + Create New Ledger
                    </Button>
                    )}
                  </div>

                  {loading ? (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="border" role="status" />
                    </div>
                  ) : (
                    <Card>
                      <Card.Header>
                        <h5 className="mb-0">Organization Ledgers</h5>
                      </Card.Header>
                      <Card.Body>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Code</th>
                              <th>Type</th>
                              <th>Category</th>
                              <th>Opening Balance</th>
                              <th>Current Balance</th>
                              <th>Linked To</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLedgers.length === 0 ? (
                              <tr>
                                <td colSpan="9" className="text-center text-muted">
                                  {ledgerFilter === 'ALL' 
                                    ? 'No ledgers found. Initialize General Ledger to get started.'
                                    : `No ${ledgerFilter} ledgers found.`}
                                </td>
                              </tr>
                            ) : (
                              filteredLedgers.map((ledger) => (
                                <tr key={ledger.id}>
                                  <td><strong>{ledger.name}</strong></td>
                                  <td>{ledger.code || '-'}</td>
                                  <td>
                                    <Badge bg={getLedgerTypeBadge(ledger.type)}>
                                      {ledger.type}
                                    </Badge>
                                  </td>
                                  <td>{ledger.category || '-'}</td>
                                  <td>{formatCurrency(ledger.openingBalance)}</td>
                                  <td><strong>{formatCurrency(ledger.currentBalance)}</strong></td>
                                  <td>
                                    {ledger.vendor && `Vendor: ${ledger.vendor.name}`}
                                    {ledger.warehouse && `Warehouse: ${ledger.warehouse.name}`}
                                    {ledger.productionUnit && `Production Unit: ${ledger.productionUnit.name}`}
                                    {ledger.retailStore && `Retail Store: ${ledger.retailStore.name}`}
                                    {ledger.bankAccount && `Bank: ${ledger.bankAccount.accountName}`}
                                    {ledger.cashInHand && `Cash: ${ledger.cashInHand.locationName}`}
                                    {!ledger.vendor && !ledger.warehouse && !ledger.productionUnit && !ledger.retailStore && !ledger.bankAccount && !ledger.cashInHand && '-'}
                                  </td>
                                  <td>
                                    {ledger.isActive ? (
                                      <Badge bg="success">Active</Badge>
                                    ) : (
                                      <Badge bg="danger">Inactive</Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      onClick={() => handleViewTransactions(ledger)}
                                      title="View Transactions"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
                                      </svg>
                                    </Button>
                                    <HistoryButton documentType="Ledger" documentId={ledger.id} label={ledger.name} className="ms-1" />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              )}
            </Tab.Pane>

            {/* Bank Accounts Tab */}
            <Tab.Pane eventKey="bank-accounts">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage organization bank accounts</p>
                {canManageAccounts && (
                <Button variant="primary" onClick={handleCreateBankAccount}>
                  + Add Bank Account
                </Button>
                )}
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Bank Accounts</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Account Name</th>
                          <th>Bank Name</th>
                          <th>Account Number</th>
                          <th>IFSC/SWIFT</th>
                          <th>Opening Balance</th>
                          <th>Current Balance</th>
                          <th>Currency</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankAccounts.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center text-muted">
                              No bank accounts found. Add one to get started.
                            </td>
                          </tr>
                        ) : (
                          bankAccounts.map((ba) => (
                            <tr key={ba.id}>
                              <td><strong>{ba.accountName}</strong></td>
                              <td>{ba.bankName}</td>
                              <td>{ba.accountNumber}</td>
                              <td>
                                {ba.ifscCode && `IFSC: ${ba.ifscCode}`}
                                {ba.swiftCode && ba.ifscCode && <br />}
                                {ba.swiftCode && `SWIFT: ${ba.swiftCode}`}
                                {!ba.ifscCode && !ba.swiftCode && '-'}
                              </td>
                              <td>{formatCurrency(ba.openingBalance)}</td>
                              <td><strong>{formatCurrency(ba.currentBalance)}</strong></td>
                              <td>{ba.currency || organizationCurrency}</td>
                              <td>
                                {ba.isActive ? (
                                  <Badge bg="success">Active</Badge>
                                ) : (
                                  <Badge bg="danger">Inactive</Badge>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(ba, 'BANK_ACCOUNT')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                {canManageAccounts && (
                                <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditBankAccount(ba)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteBankAccount(ba.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                                </>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Bank statement set against the bank account's ledger */}
            {canReconcile && (
              <Tab.Pane eventKey="bank-reconciliation">
                <BankReconciliation active={activeTab === 'bank-reconciliation'} currency={organizationCurrency}
                  canRecordReceipt={(user?.roles || []).some(isOrgAdminRole)} />
              </Tab.Pane>
            )}

            {/* Cash-in-Hand Tab */}
            <Tab.Pane eventKey="cash-in-hand">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage cash-in-hand locations</p>
                {canManageAccounts && (
                <Button variant="primary" onClick={handleCreateCashInHand}>
                  + Add Cash Location
                </Button>
                )}
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Cash-in-Hand Locations</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Location Name</th>
                          <th>Address</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Country</th>
                          <th>Postal Code</th>
                          <th>Opening Balance</th>
                          <th>Current Balance</th>
                          <th>Currency</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashInHandList.length === 0 ? (
                          <tr>
                            <td colSpan="11" className="text-center text-muted">
                              No cash-in-hand locations found. Add one to get started.
                            </td>
                          </tr>
                        ) : (
                          cashInHandList.map((cih) => (
                            <tr key={cih.id}>
                              <td><strong>{cih.locationName}</strong></td>
                              <td>{cih.address || '-'}</td>
                              <td>{cih.city || '-'}</td>
                              <td>{cih.state || '-'}</td>
                              <td>{cih.country || '-'}</td>
                              <td>{cih.postalCode || '-'}</td>
                              <td>{formatCurrency(cih.openingBalance)}</td>
                              <td><strong>{formatCurrency(cih.currentBalance)}</strong></td>
                              <td>{cih.currency || organizationCurrency}</td>
                              <td>
                                {cih.isActive ? (
                                  <Badge bg="success">Active</Badge>
                                ) : (
                                  <Badge bg="danger">Inactive</Badge>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(cih, 'CASH_IN_HAND')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                {canManageAccounts && (
                                <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditCashInHand(cih)}
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteCashInHand(cih.id)}
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                                </>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Payables: what is owed to vendors, bill by bill, and when it is due */}
            <Tab.Pane eventKey="payables">
              <Payables active={activeTab === 'payables'} currency={organizationCurrency} onPay={payVendor} reloadKey={vendorPayments.length}
                onLedger={(vendorId) => openPartyLedger(`AP-${vendorId}`)} />
            </Tab.Pane>

            {/* Vendor Payments Tab */}
            <Tab.Pane eventKey="vendor-payments">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Record and manage payments made to vendors</p>
                <Button variant="primary" onClick={handleCreatePayment}>
                  + Record Payment
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Vendor Payments</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Payment #</th>
                          <th>Date</th>
                          <th>Vendor</th>
                          <th>Amount</th>
                          {tdsEnabled && <th>TDS</th>}
                          <th>Payment Method</th>
                          <th>Source Account</th>
                          <th>Type</th>
                          <th>Applied to PO(s)</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorPayments.length === 0 ? (
                          <tr>
                            <td colSpan={tdsEnabled ? 10 : 9} className="text-center text-muted">
                              No vendor payments found. Record a payment to get started.
                            </td>
                          </tr>
                        ) : (
                          vendorPayments.map((payment) => (
                            <tr key={payment.id}>
                              <td><strong>{payment.paymentNumber}</strong></td>
                              <td>{showDate(payment.paymentDate)}</td>
                              <td>{payment.vendor?.name || '-'}</td>
                              <td><strong>{formatCurrency(payment.amount)}</strong></td>
                              {tdsEnabled && <td>{Number(payment.tdsAmount) > 0 ? formatCurrency(payment.tdsAmount) : '-'}</td>}
                              <td>{payment.paymentMethod}</td>
                              <td>{payment.sourceAccount?.name || '-'}</td>
                              <td>
                                <Badge bg={payment.paymentType === 'ADVANCE' ? 'info' : payment.paymentType === 'PO_PAYMENT' ? 'success' : 'warning'}>
                                  {payment.paymentType}
                                </Badge>
                              </td>
                              <td>
                                {payment.paymentApplications && payment.paymentApplications.length > 0 ? (
                                  <div>
                                    {payment.paymentApplications.map((app, idx) => (
                                      <div key={idx} className="small">
                                        {app.purchaseOrder?.poNumber}: {formatCurrency(app.appliedAmount)}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td>
                                <Badge bg={payment.status === 'RECORDED' ? 'success' : 'secondary'}>
                                  {payment.status}
                                </Badge>
                                {payment.status === 'CANCELLED' && payment.cancelReason && (
                                  <div className="small text-muted">{payment.cancelReason}</div>
                                )}
                                {payment.status === 'RECORDED' && canCancelPayments && (
                                  <div>
                                    <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => handleCancelPayment(payment)}>Cancel</Button>
                                  </div>
                                )}
                                <HistoryButton documentType="VendorPayment" documentId={payment.id} label={`Vendor payment ${payment.paymentNumber}`} className="ms-1" />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>
          
            {/* Expenses paid from cash or bank, no vendor or bill */}
            {canReconcile && (
              <Tab.Pane eventKey="expenses">
                <Expenses active={activeTab === 'expenses'} currency={organizationCurrency} onChanged={fetchLedgers} />
              </Tab.Pane>
            )}

            {/* Money moved between cash and bank, and journal entries */}
            {canReconcile && (
              <Tab.Pane eventKey="transfers">
                <Vouchers active={activeTab === 'transfers'} currency={organizationCurrency} canJournal={canCancelPayments} onChanged={fetchLedgers} />
              </Tab.Pane>
            )}

            {/* Receivables: aging, statements and payment reminders */}
            <Tab.Pane eventKey="receivables">
              <Receivables active={activeTab === 'receivables'} onLedger={(customerId) => openPartyLedger(`AR-${customerId}`)} />
            </Tab.Pane>

            {/* GST returns (GSTR-1 / GSTR-3B workings) */}
            <Tab.Pane eventKey="gst-returns">
              <GstReturns active={activeTab === 'gst-returns'} />
            </Tab.Pane>

            {/* Profit & Loss, Balance Sheet, Trial Balance and Day Book */}
            <Tab.Pane eventKey="financial-statements">
              <FinancialStatements active={activeTab === 'financial-statements'} currency={organizationCurrency} ledgerRequest={ledgerRequest} />
            </Tab.Pane>

            {/* Who created, changed or deleted what, and when */}
            {canSeeEditLog && (
              <Tab.Pane eventKey="edit-log">
                <div className="mt-3"><EditLog active={activeTab === 'edit-log'} /></div>
              </Tab.Pane>
            )}

            {/* What the business brought when it started on iBigDo */}
            {canEnterOpeningBalances && (
              <Tab.Pane eventKey="opening-balances">
                <OpeningBalances active={activeTab === 'opening-balances'} currency={organizationCurrency} />
              </Tab.Pane>
            )}

            {/* Tally export, and customers and vendors in from Tally or a CSV */}
            {canExchangeData && (
              <Tab.Pane eventKey="import-export">
                <div className="mt-3"><DataImportExport canImportItems={canManageAccounts} /></div>
              </Tab.Pane>
            )}

            {/* TDS kept back from vendor payments, only for a business that deducts it */}
            {tdsEnabled && canUseTds && (
              <Tab.Pane eventKey="tds">
                <Tds active={activeTab === 'tds'} currency={organizationCurrency} canManage={canManageTds}
                  onChanged={fetchLedgers} />
              </Tab.Pane>
            )}
          </Tab.Content>
          </div>
        </div>
      </Tab.Container>

      {/* Bank Account Modal */}
      <Modal show={showBankAccountModal} onHide={() => setShowBankAccountModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedBankAccount ? 'Edit Bank Account' : 'Add Bank Account'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveBankAccount}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.accountName}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, accountName: e.target.value })}
                    required
                    placeholder="e.g., Main Operating Account"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bank Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.bankName}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, bankName: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Number *</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.accountNumber}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, accountNumber: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>IFSC Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.ifscCode}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, ifscCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SWIFT Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.swiftCode}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, swiftCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Type</Form.Label>
                  <Form.Select
                    value={bankAccountFormData.accountType}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, accountType: e.target.value })}
                  >
                    <option value="">Select Type</option>
                    <option value="SAVINGS">Savings</option>
                    <option value="CURRENT">Current</option>
                    <option value="FIXED_DEPOSIT">Fixed Deposit</option>
                    <option value="RECURRING_DEPOSIT">Recurring Deposit</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Branch Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.branchName}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, branchName: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAccountFormData.currency}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, currency: e.target.value })}
                    placeholder={organizationCurrency}
                    maxLength={3}
                  />
                  <Form.Text className="text-muted">
                    Default: {organizationCurrency}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Branch Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={bankAccountFormData.branchAddress}
                onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, branchAddress: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Opening Balance</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={bankAccountFormData.openingBalance}
                    onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, openingBalance: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    A Current Asset ledger will be automatically created for this bank account
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBankAccountModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedBankAccount ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Cash-in-Hand Modal */}
      <Modal show={showCashInHandModal} onHide={() => setShowCashInHandModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedCashInHand ? 'Edit Cash-in-Hand' : 'Add Cash-in-Hand'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveCashInHand}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.locationName}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, locationName: e.target.value })}
                    required
                    placeholder="e.g., Main Office Cash"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Location Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={cashInHandFormData.locationDescription}
                onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, locationDescription: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                value={cashInHandFormData.address}
                onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, address: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.city}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, city: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.state}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, state: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.country}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, country: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.postalCode}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, postalCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Opening Balance</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={cashInHandFormData.openingBalance}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, openingBalance: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    A General Ledger account will be automatically created for this cash location
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    type="text"
                    value={cashInHandFormData.currency}
                    onChange={(e) => setCashInHandFormData({ ...cashInHandFormData, currency: e.target.value })}
                    placeholder={organizationCurrency}
                    maxLength={3}
                  />
                  <Form.Text className="text-muted">
                    Default: {organizationCurrency}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCashInHandModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedCashInHand ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>


      {/* Create Ledger Modal */}
      <Modal show={showLedgerModal} onHide={() => setShowLedgerModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Ledger</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveLedger}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Ledger Name *</Form.Label>
              <Form.Control
                type="text"
                value={ledgerFormData.name}
                onChange={(e) => setLedgerFormData({ ...ledgerFormData, name: e.target.value })}
                placeholder="Enter ledger name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ledger Type *</Form.Label>
              <Form.Select
                value={ledgerFormData.type}
                onChange={(e) => handleLedgerTypeChange(e.target.value)}
                required
              >
                <option value="">Select Type</option>
                <option value="ASSET">Asset</option>
                <option value="LIABILITY">Liability</option>
                <option value="EQUITY">Equity</option>
                <option value="REVENUE">Revenue</option>
                <option value="EXPENSE">Expense</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select the type of ledger account
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={ledgerFormData.category}
                onChange={(e) => setLedgerFormData({ ...ledgerFormData, category: e.target.value })}
                disabled={!ledgerFormData.type}
              >
                <option value="">Select Category (Optional)</option>
                {availableCategories
                  .filter(cat => cat.isActive)
                  .map(category => (
                    <option key={category.id} value={category.categoryName}>
                      {category.categoryName}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {!ledgerFormData.type 
                  ? 'Please select a ledger type first' 
                  : availableCategories.length === 0 
                    ? 'No categories defined for this type. System admin can add categories in System Admin module.'
                    : 'Select a category for this ledger'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Opening Balance ({organizationCurrency})</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={ledgerFormData.openingBalance}
                onChange={(e) => setLedgerFormData({ ...ledgerFormData, openingBalance: e.target.value })}
                placeholder="0.00"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={ledgerFormData.description}
                onChange={(e) => setLedgerFormData({ ...ledgerFormData, description: e.target.value })}
                placeholder="Optional description for this ledger"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLedgerModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Ledger
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View & Print Modal for Bank Account / Cash-in-Hand */}
      <Modal show={showViewPrintModal} onHide={() => setShowViewPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {viewPrintType === 'BANK_ACCOUNT' && 'Bank Account'}
            {viewPrintType === 'CASH_IN_HAND' && 'Cash-in-Hand'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handleViewPrintPrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowViewPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {viewPrintType === 'BANK_ACCOUNT' && viewPrintData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>BANK ACCOUNT</h2>
                <p className="text-muted"><strong>{viewPrintData.accountName}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Bank Name:</strong> {viewPrintData.bankName || '-'}</p>
                  <p><strong>Account Number:</strong> {viewPrintData.accountNumber || '-'}</p>
                  <p><strong>IFSC Code:</strong> {viewPrintData.ifscCode || '-'}</p>
                  <p><strong>SWIFT Code:</strong> {viewPrintData.swiftCode || '-'}</p>
                  <p><strong>Account Type:</strong> {viewPrintData.accountType || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Branch:</strong> {viewPrintData.branchName || '-'}</p>
                  <p><strong>Branch Address:</strong> {viewPrintData.branchAddress || '-'}</p>
                  <p><strong>Opening Balance:</strong> {formatCurrency(viewPrintData.openingBalance)}</p>
                  <p><strong>Current Balance:</strong> {formatCurrency(viewPrintData.currentBalance)}</p>
                  <p><strong>Currency:</strong> {viewPrintData.currency || organizationCurrency}</p>
                  <p><strong>Status:</strong> {viewPrintData.isActive ? 'Active' : 'Inactive'}</p>
                </Col>
              </Row>
            </div>
          )}
          {viewPrintType === 'CASH_IN_HAND' && viewPrintData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>CASH-IN-HAND</h2>
                <p className="text-muted"><strong>{viewPrintData.locationName}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Address:</strong> {viewPrintData.address || '-'}</p>
                  <p><strong>City:</strong> {viewPrintData.city || '-'}</p>
                  <p><strong>State:</strong> {viewPrintData.state || '-'}</p>
                  <p><strong>Country:</strong> {viewPrintData.country || '-'}</p>
                  <p><strong>Postal Code:</strong> {viewPrintData.postalCode || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Opening Balance:</strong> {formatCurrency(viewPrintData.openingBalance)}</p>
                  <p><strong>Current Balance:</strong> {formatCurrency(viewPrintData.currentBalance)}</p>
                  <p><strong>Currency:</strong> {viewPrintData.currency || organizationCurrency}</p>
                  <p><strong>Status:</strong> {viewPrintData.isActive ? 'Active' : 'Inactive'}</p>
                </Col>
              </Row>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Help Pane */}

      {/* Vendor Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record Vendor Payment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePayment}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor *</Form.Label>
                  <Form.Select
                    value={paymentFormData.vendor?.id || ''}
                    onChange={(e) => handleVendorChange(e.target.value)}
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Date *</Form.Label>
                  <DateInput
                   
                    value={paymentFormData.paymentDate}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {selectedVendorForPayment && (
              <Alert variant="info" className="mb-3">
                <strong>Outstanding Balance:</strong> {formatCurrency(outstandingBalance)}
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={paymentFormData.amount}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                    required
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method *</Form.Label>
                  <Form.Select
                    value={paymentFormData.paymentMethod}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentMethod: e.target.value })}
                    required
                  >
                    <option value="CHECK">Check</option>
                    <option value="ACH">ACH</option>
                    <option value="WIRE">Wire Transfer</option>
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="OTHER">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {tdsSuggestion && (
              <Row className="align-items-end mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>TDS kept back</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      value={tdsAmount}
                      onChange={(e) => { setTdsTouched(true); setTdsAmount(e.target.value); }}
                    />
                  </Form.Group>
                </Col>
                <Col md={8} className="small">
                  <div>
                    {tdsSuggestion.label}, {Number(tdsSuggestion.rate)}%{tdsSuggestion.pan ? ` (PAN ${tdsSuggestion.pan})` : ''}.{' '}
                    <strong>The bank pays {formatCurrency((parseFloat(paymentFormData.amount) || 0) - (parseFloat(tdsAmount) || 0))}</strong>;
                    {' '}the vendor is settled for the full amount.
                  </div>
                  {tdsSuggestion.note && <div className="text-muted">{tdsSuggestion.note}</div>}
                  {tdsTouched && String(tdsSuggestion.tds) !== tdsAmount && (
                    <Button variant="link" size="sm" className="p-0" onClick={() => { setTdsTouched(false); setTdsAmount(String(tdsSuggestion.tds)); }}>
                      Use the worked-out {formatCurrency(tdsSuggestion.tds)}
                    </Button>
                  )}
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Account *</Form.Label>
                  <Form.Select
                    value={paymentFormData.sourceAccount?.id || ''}
                    onChange={(e) => {
                      const account = currentAssetAccounts.find(acc => acc.id === parseInt(e.target.value));
                      setPaymentFormData({ ...paymentFormData, sourceAccount: account });
                    }}
                    required
                  >
                    <option value="">Select Source Account</option>
                    {currentAssetAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.bankAccount ? 'Bank' : 'Cash'})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select a Current Asset account (Bank Account or Cash-in-Hand)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Reference Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={paymentFormData.referenceNumber}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, referenceNumber: e.target.value })}
                    placeholder="Check number, transaction ID, etc."
                  />
                </Form.Group>
              </Col>
            </Row>

            {openBills.length > 0 && (
              <div className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <h6 className="mb-0">Bills this pays</h6>
                  <span className="small text-muted">Oldest first; change the amounts to pay other bills.</span>
                  {billsTouched && (
                    <Button size="sm" variant="link" className="p-0 ms-auto" onClick={() => setBillsTouched(false)}>Oldest first again</Button>
                  )}
                </div>
                <Table bordered size="sm" className="small mb-1">
                  <thead>
                    <tr><th>Bill</th><th>Due</th><th className="text-end">Outstanding</th><th style={{ width: '9rem' }}>Pay</th></tr>
                  </thead>
                  <tbody>
                    {openBills.map((b) => (
                      <tr key={b.id}>
                        <td>{b.vendorInvoiceNumber || b.internalNumber}<div className="text-muted">{b.internalNumber}</div></td>
                        <td className="text-nowrap">
                          {formatDate(b.dueDate)}
                          {b.daysOverdue > 0 && <div className="text-danger">{b.daysOverdue} days overdue</div>}
                          {b.msmePayBy && <div className="text-muted">MSME: by {b.msmePayBy}</div>}
                        </td>
                        <td className="text-end">{formatCurrency(b.outstanding)}</td>
                        <td>
                          <Form.Control size="sm" type="number" min="0" step="0.01" max={b.outstanding}
                            value={billAmounts[b.id] ?? ''}
                            onChange={(e) => { setBillsTouched(true); setBillAmounts((prev) => ({ ...prev, [b.id]: e.target.value })); }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="small text-muted">
                  To bills: {formatCurrency(Object.values(billAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0))}.
                  Anything not put against a bill counts as paid on account and settles the oldest bills.
                </div>
              </div>
            )}

            {loadingOpenPOs ? (
              <div className="d-flex justify-content-center mb-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : openPOs.length > 0 && (
              <div className="mb-3">
                <h6>Apply Payment to Purchase Orders (Optional)</h6>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>PO Number</th>
                      <th>PO Date</th>
                      <th>Total Amount</th>
                      <th>Paid Amount</th>
                      <th>Outstanding</th>
                      <th>Apply Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openPOs.map((po) => {
                      const existingApp = paymentFormData.poApplications.find(app => app.poId === po.id);
                      return (
                        <tr key={po.id}>
                          <td>{po.poNumber}</td>
                          <td>{showDate(po.poDate)}</td>
                          <td>{formatCurrency(po.totalAmount)}</td>
                          <td>{formatCurrency(po.paidAmount)}</td>
                          <td><strong>{formatCurrency(po.outstandingBalance)}</strong></td>
                          <td>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              max={po.outstandingBalance}
                              value={existingApp?.appliedAmount || ''}
                              onChange={(e) => handlePOApplicationChange(po.id, e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {paymentFormData.poApplications.length > 0 && (
                  <div className="mt-2">
                    {(() => {
                      const paymentAmount = parseFloat(paymentFormData.amount) || 0;
                      const totalApplied = paymentFormData.poApplications.reduce((sum, app) => {
                        return sum + (app.appliedAmount || 0);
                      }, 0);
                      const isValid = totalApplied <= paymentAmount;
                      return (
                        <Alert variant={isValid ? 'info' : 'danger'}>
                          <strong>Total Applied:</strong> {formatCurrency(totalApplied)} / {formatCurrency(paymentAmount)}
                          {!isValid && (
                            <div className="mt-1">
                              <small>⚠️ Total applied amount cannot exceed payment amount</small>
                            </div>
                          )}
                        </Alert>
                      );
                    })()}
                  </div>
                )}
                <Form.Text className="text-muted">
                  Leave blank to record as advance payment. You can apply the payment to one or more POs.
                </Form.Text>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={paymentFormData.notes}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                placeholder="Additional notes about this payment"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Record Payment
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Accounts;

